import { Suspense, lazy } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Link,
  useLocation
} from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import ErrorBoundary from './components/ErrorBoundary';
import './styles/print.css';

// Lazy load route components for code splitting
const Dashboard = lazy(() => import('./pages/Dashboard'));
const News = lazy(() => import('./pages/News'));
const Weather = lazy(() => import('./pages/Weather'));
const Econ = lazy(() => import('./pages/Econ'));
const Settings = lazy(() => import('./pages/Settings'));

// Navigation component with active state
const Navigation = () => {
  const location = useLocation();
  
  const getLinkClasses = (path) => {
    const isActive = location.pathname === path;
    return `font-semibold text-lg transition-colors px-3 py-1 ${
      isActive 
        ? 'bg-blue-100 text-blue-700 border border-blue-200 rounded' 
        : 'text-gray-700 hover:text-blue-600'
    }`;
  };

  return (
    <nav className="bg-white shadow mb-8 sticky top-0 z-50">
      <div className="container mx-auto px-4 py-4 flex justify-between items-center">
        <div className="flex items-center space-x-6">
          {/* HARVEST Capital Logo */}
          <div className="flex items-center space-x-3">
            <img 
              src="/Harvest_Avatar_RGB.png" 
              alt="HARVEST Capital Logo" 
              className="h-12 w-auto"
            />
          </div>
          
          {/* Navigation Links */}
          <div className="flex space-x-6">
            <Link to="/" className={getLinkClasses('/')}>Dashboard</Link>
            <Link to="/news" className={getLinkClasses('/news')}>News</Link>
            <Link to="/weather" className={getLinkClasses('/weather')}>Weather</Link>
            <Link to="/econ" className={getLinkClasses('/econ')}>Econ</Link>
            <Link to="/settings" className={getLinkClasses('/settings')}>Settings</Link>
          </div>
        </div>
        <div className="flex items-center space-x-4">
          <AuthenticatedUserMenu />
        </div>
      </div>
    </nav>
  );
};

// User menu component (only shown when authenticated)
const AuthenticatedUserMenu = () => {
  const { logout, currentUser } = useAuth();
  
  const handleLogout = () => {
    if (window.confirm('Are you sure you want to sign out?')) {
      logout();
    }
  };

  return (
    <div className="flex items-center space-x-4">
      <span className="text-sm text-gray-700 font-medium">
        Welcome, {currentUser || 'User'}
      </span>
      <span className="text-sm text-gray-500">
        Session expires at midnight
      </span>
      <button
        onClick={handleLogout}
        className="text-sm text-gray-700 hover:text-red-600 transition-colors px-3 py-1 hover:bg-red-50 rounded"
      >
        Sign Out
      </button>
    </div>
  );
};

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <Router>
          <ProtectedRoute>
            <AppContent />
          </ProtectedRoute>
        </Router>
      </AuthProvider>
    </ErrorBoundary>
  );
}

// Loading fallback component for lazy-loaded routes
const RouteLoadingFallback = () => (
  <div className="min-h-screen bg-gray-50 flex items-center justify-center">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
      <p className="text-gray-600">Loading page...</p>
    </div>
  </div>
);

// Separate component to access useLocation hook
function AppContent() {
  const location = useLocation();
  const isDashboard = location.pathname === '/';
  
  return (
    <div className={`min-h-screen ${isDashboard ? 'bg-gray-100' : 'bg-gray-50'}`}>
      <Navigation />
      <main className="w-full max-w-full mx-auto px-2 sm:px-4 overflow-x-hidden">
        <Suspense fallback={<RouteLoadingFallback />}>
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/news" element={<News />} />
            <Route path="/weather" element={<Weather />} />
            <Route path="/econ" element={<Econ />} />
            <Route path="/settings" element={<Settings />} />
          </Routes>
        </Suspense>
      </main>
    </div>
  );
}

export default App;